import { AnimationMetadata, AnimationBuilder, animate, AnimationFactory } from '@angular/animations';
import { Injectable, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';

import { AbstractNgAnimationService } from '../animation-service.abstract';
import { INgAnimationParams } from '../event-animation.interface';
import { NG_EVENT_TRIGGERS, INgEventTriggers } from '../event-animation.data';


/**------------------------------------------------------
 * Continuous Animation
 * --------------------
 * > The animation goes on from the start event till an end
 * > event. If the mouse if hovered over the element the
 * > animation could go on forever. So for example the hover
 * > on is the event to start the animation, and the animation
 * > continues till the end event, for example mouse leave is
 * > triggered.
 */
@Injectable()
export class NgContinuousAnimationService extends AbstractNgAnimationService {

	//** Helper Variables */
	private stopOnEvent: boolean = true;

	//** Helper Variables */
	private startListener$	: Subject<void> = new Subject<void>();
	private endListener$	: Subject<void> = new Subject<void>();

	constructor(
		private animationBuilder: AnimationBuilder,
		private renderer		: Renderer2
	) {
		super();
	}


	/**------------------------------------------------------
	 * Setup the Animation
	 */
	setupAnimation(params: INgAnimationParams): void {

		//0 - define the options
		params = this.verifyParameters(params);

		//1 - listen to the start event on the element
		const triggers 			: INgEventTriggers 	= NG_EVENT_TRIGGERS[params.event];
		const animationMetadata : AnimationMetadata = animate(`${params.timing.duration}ms ${params.timing.delay}ms`, params.states.start);

		//2 - listen and animate with start animation metadata on start event
		this.startListener$.subscribe(() => {
			const unListenStartEvent: Function = this.renderer.listen(params.triggerElement, triggers.start, () => {

				//a. remove start event listener and listen on end event
				unListenStartEvent();
				this.endListener$.next();

				//b. reset stop on event to false
				this.stopOnEvent = false;

				//c. start animation effect
				this.animateEffect(params.targetElement, [animationMetadata]);
			});
		});

		//3 - listen and animate with start animation metadata on start event
		this.endListener$.subscribe(() => {
			const unListenEndEvent: Function = this.renderer.listen(params.triggerElement, triggers.end, () => {

				//a. remove end event listener and listen on start event
				unListenEndEvent();
				this.startListener$.next();

				//b. reset stop on event to false
				this.stopOnEvent = true;

				//c. stop animation
				this.animationPlayer.finish();
			});
		});

		//4 - animate with start event
		this.startListener$.next();
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private animateEffect(targetElement: HTMLElement, animationMetadata: AnimationMetadata[]) {

		//0 - create the animation player
		const animationFactory: AnimationFactory = this.animationBuilder.build(animationMetadata);
		this.animationPlayer = animationFactory.create(targetElement);

		//1 - on animation done, destroy and create animation again
		this.animationPlayer.onDone(() => {

			//a. if a previous one exist, destroy it
			this.animationPlayer?.destroy();

			//b. animate effect again if no stop event occurs
			if (!this.stopOnEvent) this.animateEffect(targetElement, animationMetadata);
		});

		//2 - animate the effect
		this.animationPlayer.play();
	}

	protected verifyCustomParameters(params: INgAnimationParams): INgAnimationParams {
		if (!params?.states?.start) throw new Error(`NgContinuousAnimationService => verifyParameters => FATAL ERROR: The animation states are required for defining the animation behavior, the starting state is undefined (start animation state: ${params?.states?.start})`);
		return params;
	}
}
